import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { formatPercentage, useSelect, useSwitch } from '@chilecompra/react-kit'
import { DataTable, Select, Switch } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetProviderTradedAmountsThunk } from './ProviderTradedAmountsSection.actions'
import PieChart from '../../components/Charts/PieChart'
import RegionalAmountsTradedNationalLevel from '../../components/Infographics/RegionalAmountsTradedNationalLevel'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Description,
  GridContainer,
  GridItem,
  Info,
  Root,
  Strong,
  Title,
  Total
} from './ProviderTradedAmountsSection.styles'

/**
 * The ProviderTradedAmountsSection's container.
 */
const ProviderTradedAmountsSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const {
    endYear,
    latestUpdate,
    loading,
    modalities: mechanismOptions,
    startYear
  } = useSelector(state => state.topNavbar)
  const { dni, name } = useSelector(state => state.providerSection)
  const { csv, data, totalAmount, totalAmountUSD, totalOrders } = useSelector(
    state => state.providerTradedAmountsSection
  )
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: mechanismValue, onChange: handleChangeMechanism } = useSelect({ initialValue: 7 })
  const { value: currencyValue, onChange: handleChangeNationalLevelCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () => exportToImage('provider-traded-amounts', `Info_Proveedor_Monto_Transado_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Region_Monto_Transado_Nivel_Nacional_${yearValue}`)
  })
  const handlePieChartTooltipFormatter = useCallback(
    function () {
      return `
        <p>
          <strong>${name}</strong> en el sector <strong>${this.key}</strong> en el <strong>año ${yearValue}</strong>:
        </p>
        <br />
        <br />
        <ul>
          <li>
            - <strong>${currencyValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP} ${formatAmount(
              this.point.y
            )}</strong> transados.
          </li>
          <br />
          <li>
            - Este corresponde al <strong>${formatPercentage(
              this.point?.percentage
            )}</strong> de su monto total transado durante el año.
          </li>
          <br />
          <li>
            - <strong>${this.point.orders}</strong> órdenes de compra.
          </li>
        </ul>
      `
    },
    [currencyValue, name, yearValue]
  )
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const enhancedData = useMemo(
    () =>
      data.map(datum => ({
        ...datum,
        color: theme.palette.brand[datum.colorName],
        y: currencyValue ? datum?.amountUSD : datum?.amount
      })),
    [currencyValue, data, dni]
  )

  useEffect(() => {
    if (!dni || !mechanismValue || !yearValue) {
      return
    }

    dispatch(onGetProviderTradedAmountsThunk({ dni, mechanismId: mechanismValue, year: yearValue }))
  }, [dni, mechanismValue, yearValue])

  return (
    <Root>
      <GridContainer padding="35px 0 23px">
        <GridItem xs={12}>
          <Title>Montos transados en cada sector del Estado</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Compara el monto total transado del proveedor en cada uno de los sectores del Estado, según el año y
            mecanismo de compra seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <DataLayout
        disableCaptureImage={disableCaptureImage}
        disableDownloadFile={disableDownloadFile}
        header={
          <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
            <GridItem md={4} xs={12}>
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={6} xs={12}>
                  <Select
                    label="AÑO"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
                <GridItem md={6} xs={12}>
                  <Select
                    label="MECANISMO DE COMPRA"
                    loading={loading}
                    options={mechanismOptions}
                    value={mechanismValue}
                    onChange={handleChangeMechanism}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem md={2} xs={12}>
              <Switch
                checked={currencyValue}
                color="primary"
                labelLeft="CLP"
                labelRight="USD"
                onChange={handleChangeNationalLevelCurrency}
              />
            </GridItem>
          </GridContainer>
        }
        infographic={<RegionalAmountsTradedNationalLevel />}
        noData={enhancedData.length === 0}
        ref={dataLayoutRef}
        updatedDate={latestUpdate}
        onCaptureImage={handleCaptureImage}
        onChangeView={handleChangeView}
        onDownloadFile={handleDownloadFile}
      >
        <DataLayoutChild view={DataLayoutViews.CHART}>
          <GridContainer>
            <GridItem xs={12}>
              <PieChart
                data={enhancedData}
                hoverColor={theme.palette.charts.darkRed}
                id="regional-amounts-traded-pie-chart"
                tooltipFormatter={handlePieChartTooltipFormatter}
              />
            </GridItem>
          </GridContainer>
          <GridContainer>
            <GridItem align="center" xs={12}>
              <Total>
                Total nacional:{' '}
                <Strong>
                  {currencyValue
                    ? `${CURRENCY_VALUES.USD} ${formatAmount(totalAmountUSD)}`
                    : `${CURRENCY_VALUES.CLP} ${formatAmount(totalAmount)}`}
                </Strong>
              </Total>
            </GridItem>
          </GridContainer>
          <GridContainer margin="4px 0 0">
            <GridItem align="center" xs={12}>
              <Total>
                Total órdenes de compra: <Strong>{totalOrders}</Strong>
              </Total>
            </GridItem>
          </GridContainer>
          <GridContainer margin="30px 0 0">
            <GridItem xs={12}>
              <Info>
                La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                impuestos)
              </Info>
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
        <DataLayoutChild view={DataLayoutViews.TABLE}>
          <GridContainer>
            <GridItem xs={12}>
              <DataTable
                headers={[
                  { label: 'Año', value: 'year' },
                  { label: 'Región', value: 'name' },
                  { label: 'Monto transado', value: 'amount' },
                  { label: 'Monto transado (USD)', value: 'amountUSD' },
                  { label: 'N° de órdenes de compra', value: 'orders' }
                ]}
                headersCellsProps={[
                  { enableSort: true, width: '10%' },
                  { enableSort: true },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '20%' }
                ]}
                loading={loading}
                records={enhancedData}
                recordsDecorators={[
                  ({ year }) => year,
                  ({ name }) => name,
                  ({ amount }) => `$${formatAmount(amount)}`,
                  ({ amountUSD }) => `USD ${formatAmount(amountUSD)}`,
                  ({ orders }) => orders
                ]}
              />
            </GridItem>
          </GridContainer>
          <GridContainer margin="30px 0 0">
            <GridItem xs={12}>
              <Info>
                La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                impuestos)
              </Info>
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
      </DataLayout>
    </Root>
  )
}

export default ProviderTradedAmountsSection
